import Phaser from 'phaser';

import {
  ARENA_CONSTANTS,
  advanceArena,
  createArenaResult,
  createArenaRun,
  type ArenaDirection,
  type ArenaEnemyKind,
  type ArenaRun,
} from '../domain/arena-rules';
import {
  MAX_SIMULATION_DELTA_MS,
  clampPilotToArena,
  createPilotSimulation,
  stepPilotSimulation,
  type PilotSimulationState,
} from '../domain/pilot-simulation';
import { formatInputSources, formatPauseReasons } from '../i18n/dynamic-labels';
import { translate, type Locale, type ResourceKey } from '../i18n/resources';
import { LOCAL_STATE, type LocalSettings } from '../persistence/local-state';
import { GAME_RUNTIME, resolveCombatIntent } from '../runtime/game-runtime';
import { SETTINGS } from './accessibility-settings';
import { SCENE_KEYS } from './scene-contract';
import {
  announceCaption,
  announceStatus,
  createHudSnapshot,
  hideHud,
  renderHud,
  resultAnnouncement,
} from './hud';

const BACKGROUND = 0x07111f;
const PANEL = 0x0e2034;
const CYAN = '#54e8ff';
const YELLOW = '#ffe36d';
const RED = '#ff6b7a';
const GREEN = '#7dff9b';
const ARENA_CENTER = { x: 640, y: 360 } as const;
const ARENA_BOUNDS = { left: 80, right: 1_200, top: 105, bottom: 620 } as const;
const ARENA_SEED = 73;
const ENEMY_LABEL_KEYS: Readonly<Record<ArenaEnemyKind, ResourceKey>> = {
  chaser: 'level.chaser',
  ranged: 'level.ranged',
  elite: 'level.elite',
};

type ArenaResult = ReturnType<typeof createArenaResult> & { readonly qaOnly?: boolean };

const activeGamepad = (): Gamepad | null =>
  navigator.getGamepads?.().find((gamepad): gamepad is Gamepad => gamepad !== null) ?? null;

const sceneText = (
  locale: Locale,
  key: ResourceKey,
  values: Readonly<Record<string, string | number>> = {},
): string => translate(locale, key, values);

const sceneFontSize = (base: number): string =>
  `${Math.round(base * (SETTINGS.load().accessibility.largeText ? 1.25 : 1))}px`;

const sceneBackground = (): number =>
  SETTINGS.load().accessibility.highContrast ? 0x000000 : BACKGROUND;
const scenePanel = (): number => (SETTINGS.load().accessibility.highContrast ? 0x000000 : PANEL);

const movementLabel = (locale: Locale): string =>
  sceneText(
    locale,
    SETTINGS.load().controlScheme === 'ijkl' ? 'settings.schemeIjkl' : 'settings.schemeWasd',
  );

const displaySignature = (settings: LocalSettings): string =>
  JSON.stringify({
    language: settings.language,
    controlScheme: settings.controlScheme,
    largeText: settings.accessibility.largeText,
    highContrast: settings.accessibility.highContrast,
    reducedMotion: settings.accessibility.reducedMotion,
  });

const bindSceneSettings = (scene: Phaser.Scene, initial: LocalSettings): void => {
  let signature = displaySignature(initial);
  const unsubscribe = SETTINGS.subscribe((settings) => {
    const next = displaySignature(settings);
    if (next === signature) return;
    signature = next;
    if (scene.scene.isActive()) scene.scene.restart({ settingsRefresh: true });
  });
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, unsubscribe);
};

const playPlaceholderTone = (frequency: number): void => {
  const settings = SETTINGS.load();
  if (settings.audio.muted || settings.audio.masterVolume === 0 || settings.audio.effectsVolume === 0) return;
  const context = new AudioContext();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  const volume = (settings.audio.masterVolume / 100) * (settings.audio.effectsVolume / 100) * 0.08;
  oscillator.frequency.value = frequency;
  gain.gain.setValueAtTime(volume, context.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.09);
  oscillator.connect(gain).connect(context.destination);
  oscillator.start();
  oscillator.stop(context.currentTime + 0.1);
  oscillator.addEventListener('ended', () => void context.close(), { once: true });
};

const toArenaPosition = (x: number, y: number) => ({
  x: x - ARENA_CENTER.x,
  y: y - ARENA_CENTER.y,
});

const toScreenPosition = (position: { readonly x: number; readonly y: number }) => ({
  x: ARENA_CENTER.x + position.x,
  y: ARENA_CENTER.y + position.y,
});

const distance = (
  left: { readonly x: number; readonly y: number },
  right: { readonly x: number; readonly y: number },
): number => Math.hypot(left.x - right.x, left.y - right.y);

class ShellScene extends Phaser.Scene {
  public constructor(key: string) {
    super(key);
  }
}

export class BootScene extends Phaser.Scene {
  public constructor() {
    super(SCENE_KEYS.boot);
  }

  public create(): void {
    this.scene.start(SCENE_KEYS.preload);
  }
}

export class PreloadScene extends Phaser.Scene {
  public constructor() {
    super(SCENE_KEYS.preload);
  }

  public create(): void {
    this.scene.start(SCENE_KEYS.mainMenu);
  }
}

export class MainMenuScene extends ShellScene {
  public constructor() {
    super(SCENE_KEYS.mainMenu);
  }

  public create(): void {
    const locale = SETTINGS.load().language;
    hideHud(document);
    this.cameras.main.setBackgroundColor(sceneBackground());
    this.add.text(640, 245, sceneText(locale, 'menu.title'), {
      color: CYAN,
      fontFamily: 'system-ui, sans-serif',
      fontSize: sceneFontSize(54),
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.add.text(640, 360, sceneText(locale, 'menu.instructions', { movement: movementLabel(locale) }), {
      align: 'center',
      color: '#f3fbff',
      fontFamily: 'system-ui, sans-serif',
      fontSize: sceneFontSize(25),
      lineSpacing: 10,
    }).setOrigin(0.5);
    bindSceneSettings(this, SETTINGS.load());
  }

  public update(): void {
    if (GAME_RUNTIME.readFrame(activeGamepad()).actions.includes('confirm')) {
      this.scene.start(SCENE_KEYS.tutorial);
    }
  }
}

export class TutorialScene extends ShellScene {
  public constructor() {
    super(SCENE_KEYS.tutorial);
  }

  public create(): void {
    const locale = SETTINGS.load().language;
    hideHud(document);
    this.cameras.main.setBackgroundColor(sceneBackground());
    this.add.rectangle(640, 360, 1_020, 500, scenePanel()).setStrokeStyle(4, 0x54e8ff);
    this.add.text(640, 175, sceneText(locale, 'tutorial.title'), {
      color: CYAN,
      fontFamily: 'system-ui, sans-serif',
      fontSize: sceneFontSize(42),
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.add.text(640, 340, sceneText(locale, 'tutorial.instructions', { movement: movementLabel(locale) }), {
      align: 'left',
      color: '#f3fbff',
      fontFamily: 'system-ui, sans-serif',
      fontSize: sceneFontSize(29),
      lineSpacing: 14,
    }).setOrigin(0.5);
    this.add.text(640, 535, sceneText(locale, 'tutorial.readyPrompt'), {
      color: YELLOW,
      fontFamily: 'system-ui, sans-serif',
      fontSize: sceneFontSize(27),
      fontStyle: 'bold',
    }).setOrigin(0.5);
    bindSceneSettings(this, SETTINGS.load());
  }

  public update(): void {
    if (GAME_RUNTIME.readFrame(activeGamepad()).actions.includes('confirm')) {
      this.scene.start(SCENE_KEYS.level);
    }
  }
}

export class LevelScene extends ShellScene {
  #simulation: PilotSimulationState = createPilotSimulation(ARENA_CENTER);
  #run: ArenaRun = createArenaRun(ARENA_SEED);
  #pilot!: Phaser.GameObjects.Container;
  #pauseLabel!: Phaser.GameObjects.Text;
  #statusLabel!: Phaser.GameObjects.Text;
  #teachingLabel!: Phaser.GameObjects.Text;
  #feedbackLabel!: Phaser.GameObjects.Text;
  #enemyObjects = new Map<string, Phaser.GameObjects.Container>();
  #hazardObjects = new Map<string, Phaser.GameObjects.Container>();
  #pickupObjects = new Map<string, Phaser.GameObjects.Container>();
  #damagedThreatIds = new Set<string>();
  #moved = false;
  #attacked = false;
  #collected = false;
  #dashed = false;
  #preserveRunOnCreate = false;

  public constructor() {
    super(SCENE_KEYS.level);
  }

  public init(data?: { readonly settingsRefresh?: boolean }): void {
    this.#preserveRunOnCreate = data?.settingsRefresh === true;
  }

  public create(): void {
    const settings = SETTINGS.load();
    const locale = settings.language;
    if (!this.#preserveRunOnCreate) {
      this.#simulation = createPilotSimulation(ARENA_CENTER);
      this.#run = createArenaRun(ARENA_SEED);
      this.#damagedThreatIds.clear();
      this.#moved = false;
      this.#attacked = false;
      this.#collected = false;
      this.#dashed = false;
    }
    this.#preserveRunOnCreate = false;
    this.#enemyObjects.clear();
    this.#hazardObjects.clear();
    this.#pickupObjects.clear();
    announceCaption(document, '', settings.accessibility.captions);
    announceStatus(document, sceneText(locale, 'status.levelStarted'));
    this.cameras.main.setBackgroundColor(sceneBackground());
    this.add.rectangle(640, 360, 1_120, 510, scenePanel()).setStrokeStyle(4, 0x54e8ff);
    this.add.text(640, 92, sceneText(locale, 'level.title'), {
      color: CYAN,
      fontFamily: 'system-ui, sans-serif',
      fontSize: sceneFontSize(28),
      fontStyle: 'bold',
    }).setOrigin(0.5);

    const pilotBody = this.add.circle(0, 0, 24, 0xffe36d).setStrokeStyle(6, 0x172033);
    const pilotNose = this.add.triangle(0, -32, 0, 18, 12, 0, 24, 18, 0x54e8ff);
    this.#pilot = this.add.container(this.#simulation.pilot.x, this.#simulation.pilot.y, [pilotBody, pilotNose]);
    this.#statusLabel = this.add.text(96, 118, '', {
      color: '#f3fbff',
      fontFamily: 'system-ui, sans-serif',
      fontSize: sceneFontSize(17),
    });
    this.#teachingLabel = this.add.text(640, 580, '', {
      align: 'center',
      backgroundColor: '#050b14dd',
      color: YELLOW,
      fontFamily: 'system-ui, sans-serif',
      fontSize: sceneFontSize(21),
      fontStyle: 'bold',
      padding: { x: 14, y: 8 },
    }).setOrigin(0.5).setDepth(8);
    this.#feedbackLabel = this.add.text(640, 145, sceneText(locale, 'level.feedback'), {
      align: 'center',
      color: '#ffffff',
      fontFamily: 'system-ui, sans-serif',
      fontSize: sceneFontSize(19),
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(8);
    this.#pauseLabel = this.add.text(640, 360, '', {
      align: 'center',
      backgroundColor: '#050b14ee',
      color: YELLOW,
      fontFamily: 'system-ui, sans-serif',
      fontSize: sceneFontSize(58),
      fontStyle: 'bold',
      padding: { x: 48, y: 28 },
    }).setOrigin(0.5).setDepth(20).setVisible(false);
    this.#syncEntities(locale);
    bindSceneSettings(this, settings);
  }

  #announceCue(key: ResourceKey, frequency: number, values: Readonly<Record<string, string | number>> = {}): void {
    const settings = SETTINGS.load();
    const message = sceneText(settings.language, key, values);
    this.#feedbackLabel.setText(message).setAlpha(1);
    announceCaption(document, message, settings.accessibility.captions);
    playPlaceholderTone(frequency);
    if (!settings.accessibility.reducedMotion) {
      this.tweens.add({ targets: this.#feedbackLabel, alpha: 0.35, duration: 700, yoyo: true });
    }
  }

  #createEnemy(kind: ArenaEnemyKind, x: number, y: number, locale: Locale): Phaser.GameObjects.Container {
    const color = kind === 'elite' ? 0xff6b7a : kind === 'ranged' ? 0xb388ff : 0x54e8ff;
    const radius = kind === 'elite' ? 32 : 24;
    const body = this.add.circle(0, 0, radius, color, 0.92).setStrokeStyle(5, 0xffffff);
    const symbol = this.add.text(0, 0, kind === 'elite' ? '★' : kind === 'ranged' ? '◎' : '◆', {
      color: '#07111f',
      fontFamily: 'system-ui, sans-serif',
      fontSize: sceneFontSize(kind === 'elite' ? 28 : 22),
      fontStyle: 'bold',
    }).setOrigin(0.5);
    const label = this.add.text(0, radius + 15, sceneText(locale, ENEMY_LABEL_KEYS[kind]), {
      color: '#ffffff',
      fontFamily: 'system-ui, sans-serif',
      fontSize: sceneFontSize(14),
      fontStyle: 'bold',
    }).setOrigin(0.5);
    return this.add.container(x, y, [body, symbol, label]);
  }

  #syncEntities(locale: Locale): void {
    const enemyIds = new Set(this.#run.enemies.map(({ id }) => id));
    for (const [id, object] of this.#enemyObjects) {
      if (!enemyIds.has(id)) {
        object.destroy(true);
        this.#enemyObjects.delete(id);
      }
    }
    for (const enemy of this.#run.enemies) {
      const position = toScreenPosition(enemy.position);
      let object = this.#enemyObjects.get(enemy.id);
      if (object === undefined) {
        object = this.#createEnemy(enemy.kind, position.x, position.y, locale);
        this.#enemyObjects.set(enemy.id, object);
      }
      object.setPosition(position.x, position.y);
      object.setAlpha(enemy.actionState === 'damaging' ? 1 : enemy.actionState === 'telegraph' ? 0.62 : 0.9);
    }

    const hazardIds = new Set(this.#run.hazards.map(({ id }) => id));
    for (const [id, object] of this.#hazardObjects) {
      if (!hazardIds.has(id)) {
        object.destroy(true);
        this.#hazardObjects.delete(id);
      }
    }
    for (const hazard of this.#run.hazards) {
      const position = toScreenPosition(hazard.position);
      const existing = this.#hazardObjects.get(hazard.id);
      if (existing !== undefined) {
        existing.setPosition(position.x, position.y);
        const label = existing.getAt(1) as Phaser.GameObjects.Text;
        label.setText(`${sceneText(locale, 'level.hazard')}\n${hazard.state === 'damaging' ? '◆ DAMAGE' : sceneText(locale, 'level.telegraph')}`);
        existing.setAlpha(hazard.state === 'idle' ? 0.45 : 1);
        continue;
      }
      const ring = this.add.circle(0, 0, 42, 0xff6b7a, 0.18).setStrokeStyle(6, 0xff6b7a);
      const label = this.add.text(
        0,
        0,
        `${sceneText(locale, 'level.hazard')}\n${sceneText(locale, 'level.telegraph')}`,
        {
          align: 'center',
          color: '#ffffff',
          fontFamily: 'system-ui, sans-serif',
          fontSize: sceneFontSize(14),
          fontStyle: 'bold',
        },
      ).setOrigin(0.5);
      const object = this.add.container(position.x, position.y, [ring, label]);
      this.#hazardObjects.set(hazard.id, object);
      if (!SETTINGS.load().accessibility.reducedMotion) {
        this.tweens.add({ targets: ring, alpha: 0.35, scale: 1.12, duration: 650, yoyo: true, repeat: -1 });
      }
    }

    const pickupIds = new Set(this.#run.pickups.map(({ id }) => id));
    for (const [id, object] of this.#pickupObjects) {
      if (!pickupIds.has(id)) {
        object.destroy(true);
        this.#pickupObjects.delete(id);
      }
    }
    for (const pickup of this.#run.pickups) {
      let object = this.#pickupObjects.get(pickup.id);
      if (object === undefined) {
        const position = toScreenPosition(pickup.position);
        const body = this.add.circle(0, 0, 15, 0x7dff9b).setStrokeStyle(4, 0xffffff);
        const label = this.add.text(0, 30, sceneText(locale, 'level.pickup'), {
          color: '#7dff9b',
          fontFamily: 'system-ui, sans-serif',
          fontSize: sceneFontSize(14),
          fontStyle: 'bold',
        }).setOrigin(0.5);
        object = this.add.container(position.x, position.y, [body, label]);
        this.#pickupObjects.set(pickup.id, object);
      }
      if (pickup.magnetized) object.setScale(1.18);
      const position = toScreenPosition(pickup.position);
      object.setPosition(position.x, position.y);
    }
  }

  #collisionDamage(): { readonly amount: number; readonly threatId?: string } {
    const player = this.#run.playerPosition;
    const enemy = this.#run.enemies.find(
      ({ id, kind, position, actionState }) => !this.#damagedThreatIds.has(id) && distance(player, position) <= 34 && (kind === 'chaser' || actionState === 'damaging'),
    );
    if (enemy !== undefined) return { amount: 1, threatId: enemy.id };
    const hazard = this.#run.hazards.find(
      ({ id, position, state }) => !this.#damagedThreatIds.has(id) && state === 'damaging' && distance(player, position) <= 44,
    );
    return hazard === undefined ? { amount: 0 } : { amount: 1, threatId: hazard.id };
  }

  #renderTeaching(locale: Locale): void {
    const key: ResourceKey = !this.#moved
      ? 'tutorial.movePrompt'
      : !this.#attacked
        ? 'tutorial.attackPrompt'
        : !this.#collected
          ? 'tutorial.pickupPrompt'
          : !this.#dashed
            ? 'tutorial.dashPrompt'
            : 'tutorial.completePrompt';
    this.#teachingLabel.setText(sceneText(locale, key));
  }

  #renderDashCue(direction: ArenaDirection): void {
    const reducedMotion = SETTINGS.load().accessibility.reducedMotion;
    const trail = this.add.line(
      this.#pilot.x,
      this.#pilot.y,
      0,
      0,
      -direction.x * 72,
      -direction.y * 72,
      0xffe36d,
      0.9,
    ).setLineWidth(8).setDepth(3);
    if (reducedMotion) {
      trail.setAlpha(0.75);
      this.time.delayedCall(350, () => trail.destroy());
    } else {
      this.tweens.add({ targets: trail, alpha: 0, duration: 280, onComplete: () => trail.destroy() });
    }
  }

  public update(_time: number, delta: number): void {
    const frame = GAME_RUNTIME.readFrame(activeGamepad());
    for (const action of frame.actions) {
      if (action === 'pause') GAME_RUNTIME.pause.toggleUserPause();
      if (action === 'mute' || action === 'volume-up' || action === 'volume-down') {
        const changed = SETTINGS.applyAudioAction(action);
        const key = action === 'mute'
          ? changed.audio.muted ? 'caption.muted' : 'caption.unmuted'
          : 'caption.volume';
        announceCaption(
          document,
          sceneText(changed.language, key, { value: changed.audio.masterVolume }),
          changed.accessibility.captions,
        );
      }
    }

    const settings = SETTINGS.load();
    const locale = settings.language;
    const paused = GAME_RUNTIME.pause.isPaused();
    this.#pauseLabel.setText(
      paused
        ? sceneText(locale, 'level.paused', {
            reasons: formatPauseReasons(locale, GAME_RUNTIME.pause.getReasons()).join(' + '),
          })
        : '',
    ).setVisible(paused);

    const simulationDelta = Math.min(delta, MAX_SIMULATION_DELTA_MS);
    this.#simulation = stepPilotSimulation(this.#simulation, frame.movement, simulationDelta, paused);
    const pilot = clampPilotToArena(this.#simulation.pilot, ARENA_BOUNDS);
    this.#simulation = { ...this.#simulation, pilot };
    const intent = resolveCombatIntent(frame, this.#run.facing);
    const collision = this.#collisionDamage();
    const previous = this.#run;
    this.#run = advanceArena(this.#run, {
      deltaMilliseconds: simulationDelta,
      paused,
      playerPosition: toArenaPosition(pilot.x, pilot.y),
      movement: frame.movement,
      ...(intent.attackDirection === undefined ? {} : { attackDirection: intent.attackDirection }),
      ...(intent.dashDirection === undefined ? {} : { dashDirection: intent.dashDirection }),
      teachingComplete: this.#moved && this.#attacked && this.#collected && this.#dashed,
      forcedDamage: collision.amount,
    });
    if (collision.threatId !== undefined && this.#run.health < previous.health) {
      this.#damagedThreatIds.add(collision.threatId);
    }

    if (!paused && (frame.movement.x !== 0 || frame.movement.y !== 0)) this.#moved = true;
    if (this.#run.enemies.length < previous.enemies.length) {
      this.#attacked = true;
      this.#announceCue('caption.attackHit', 520);
    }
    if (this.#run.pickups.length < previous.pickups.length) {
      this.#collected = true;
      this.#announceCue('caption.pickup', 760);
    }
    if (this.#run.lastDashAtMilliseconds !== previous.lastDashAtMilliseconds) {
      this.#dashed = true;
      this.#renderDashCue(intent.facing);
      this.#announceCue('caption.dash', 640);
    }
    if (this.#run.health < previous.health) this.#announceCue('caption.playerDamage', 180);
    if (this.#run.wave > previous.wave) {
      this.#announceCue('caption.wave', 880, { wave: this.#run.wave });
    }

    const displacedPilot = clampPilotToArena(toScreenPosition(this.#run.playerPosition), ARENA_BOUNDS);
    this.#simulation = { ...this.#simulation, pilot: displacedPilot };
    this.#pilot.setPosition(displacedPilot.x, displacedPilot.y);
    this.#pilot.setRotation(Math.atan2(intent.facing.y, intent.facing.x) + Math.PI / 2);
    this.#syncEntities(locale);
    this.#renderTeaching(locale);

    const comboRemaining = Math.max(
      0,
      (this.#run.comboExpiresAtMilliseconds ?? this.#run.activeElapsedMilliseconds) - this.#run.activeElapsedMilliseconds,
    );
    const dashRemaining = Math.max(
      0,
      this.#run.lastDashAtMilliseconds + ARENA_CONSTANTS.dashCooldownMilliseconds - this.#run.activeElapsedMilliseconds,
    );
    renderHud(document, createHudSnapshot({
      timeRemainingMilliseconds: ARENA_CONSTANTS.timeCapMilliseconds - this.#run.activeElapsedMilliseconds,
      health: this.#run.health,
      maximumHealth: ARENA_CONSTANTS.startingHealth,
      wave: this.#run.wave,
      totalWaves: ARENA_CONSTANTS.waveCount,
      combo: this.#run.combo,
      comboRemainingMilliseconds: comboRemaining,
      dashRemainingMilliseconds: dashRemaining,
      score: this.#run.score,
      result: this.#run.status,
      paused,
      pauseReasons: GAME_RUNTIME.pause.getReasons(),
    }, locale));
    this.#statusLabel.setText(
      `${sceneText(locale, 'hud.waveValue', { current: this.#run.wave, total: ARENA_CONSTANTS.waveCount })} · ` +
      `${sceneText(locale, 'hud.score')}: ${this.#run.score} · ×${this.#run.combo}\n` +
      `${formatInputSources(locale, frame.activeSources) || sceneText(locale, 'level.inputIdle')}`,
    );

    if (this.#run.status !== 'playing') {
      this.scene.start(SCENE_KEYS.results, createArenaResult(this.#run));
    }
  }
}

export class PauseOverlayScene extends ShellScene {
  public constructor() {
    super(SCENE_KEYS.pauseOverlay);
  }
}

export class ResultsScene extends ShellScene {
  #result: ArenaResult | undefined;

  public constructor() {
    super(SCENE_KEYS.results);
  }

  public init(result: ArenaResult): void {
    this.#result = result;
  }

  public create(): void {
    const result = this.#result;
    if (result === undefined) {
      this.scene.start(SCENE_KEYS.mainMenu);
      return;
    }
    const won = result.outcome === 'won';
    const locale = SETTINGS.load().language;
    if (!result.qaOnly) LOCAL_STATE.recordOutcome('level-1', result.outcome);
    renderHud(document, createHudSnapshot({
      timeRemainingMilliseconds: Math.max(0, ARENA_CONSTANTS.timeCapMilliseconds - result.activeElapsedMilliseconds),
      health: result.healthRemaining,
      maximumHealth: ARENA_CONSTANTS.startingHealth,
      wave: result.wave,
      totalWaves: ARENA_CONSTANTS.waveCount,
      combo: 1,
      comboRemainingMilliseconds: 0,
      dashRemainingMilliseconds: 0,
      score: result.score,
      result: result.outcome,
      paused: false,
      pauseReasons: [],
    }, locale));
    announceStatus(document, resultAnnouncement(result.outcome, result.score, result.lossReason, locale));
    this.cameras.main.setBackgroundColor(sceneBackground());
    this.add.rectangle(640, 360, 960, 500, scenePanel()).setStrokeStyle(5, won ? 0x7dff9b : 0xff6b7a);
    this.add.text(640, 205, sceneText(locale, won ? 'results.winTitle' : 'results.lossTitle'), {
      color: won ? GREEN : RED,
      fontFamily: 'system-ui, sans-serif',
      fontSize: sceneFontSize(56),
      fontStyle: 'bold',
    }).setOrigin(0.5);
    const seconds = Math.ceil(result.activeElapsedMilliseconds / 1_000);
    const detail = sceneText(locale, won ? 'results.winDetail' : 'results.lossDetail', {
      seconds,
      score: result.score,
      cause: sceneText(locale, result.lossReason === 'time-cap' ? 'results.timerExpired' : 'results.healthDepleted'),
    });
    this.add.text(640, 350, detail, {
      align: 'center',
      color: '#f3fbff',
      fontFamily: 'system-ui, sans-serif',
      fontSize: sceneFontSize(30),
      lineSpacing: 12,
    }).setOrigin(0.5);
    this.add.text(640, 510, `${sceneText(locale, 'results.retry')} · ${sceneText(locale, 'results.restart')}`, {
      color: YELLOW,
      fontFamily: 'system-ui, sans-serif',
      fontSize: sceneFontSize(23),
      fontStyle: 'bold',
    }).setOrigin(0.5);
    playPlaceholderTone(won ? 980 : 140);
    bindSceneSettings(this, SETTINGS.load());
  }

  public update(): void {
    if (GAME_RUNTIME.readFrame(activeGamepad()).actions.includes('confirm')) {
      this.scene.start(SCENE_KEYS.level);
    }
  }
}

export class SettingsScene extends ShellScene {
  public constructor() {
    super(SCENE_KEYS.settings);
  }
}

export const GAME_SCENES = [
  BootScene,
  PreloadScene,
  MainMenuScene,
  TutorialScene,
  LevelScene,
  PauseOverlayScene,
  ResultsScene,
  SettingsScene,
];
